/**
 * EmployeeSafetyTab.jsx — Fascicolo Sicurezza del lavoratore (Release 1.1.0, FASE B)
 *
 * Sotto-tab della scheda dipendente. Presenta:
 *  · Cruscotto (4 KPI): Sorveglianza · Formazione · Incarichi · Prossima scadenza
 *  · Tab operativi: Sorveglianza sanitaria / Formazione / Incarichi
 *  · Dialog di creazione/rinnovo tramite SafeFormDialog
 *  · Deep-link `?section=&record=` per apertura mirata + evidenziazione
 *
 * Regole GDPR:
 *  · Nessun dato clinico/diagnosi/anamnesi memorizzato
 *  · I toast NON contengono giudizi, prescrizioni o note sanitarie
 *  · L'accesso alla sezione sanitaria è gestito lato backend (flag azienda
 *    `employee_can_see_own_health`): se 403, mostriamo un placeholder neutro.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SafeFormDialog } from "@/components/SafeFormDialog";
import {
  Plus, RefreshCw, Trash2, FileText, ShieldAlert,
  Stethoscope, GraduationCap, UserCheck, CalendarClock,
} from "lucide-react";
import { formatDateIT } from "@/foundation/formatting";
import { DurationInput, DurationFormatBadge } from "@/foundation/duration";

/* ------------------------------------------------------------------ */
/* Costanti UI                                                         */
/* ------------------------------------------------------------------ */
const STATUS_TONE = {
  regular:      "bg-emerald-100 text-emerald-800 border-emerald-300",
  expiring_90:  "bg-yellow-100 text-yellow-800 border-yellow-300",
  expiring_60:  "bg-amber-100 text-amber-800 border-amber-300",
  expiring_30:  "bg-orange-100 text-orange-800 border-orange-300",
  expired:      "bg-red-100 text-red-800 border-red-300",
  no_expiry:    "bg-slate-100 text-slate-700 border-slate-300",
  missing:      "bg-slate-100 text-slate-700 border-slate-300",
};
const STATUS_LABEL = {
  regular: "Regolare",
  expiring_90: "Entro 90gg",
  expiring_60: "Entro 60gg",
  expiring_30: "Entro 30gg",
  expired: "Scaduto",
  no_expiry: "Senza scadenza",
  missing: "Assente",
};
const COURSE_TYPES = [
  ["general", "Formazione generale"],
  ["specific", "Formazione specifica"],
  ["refresher_worker", "Aggiornamento lavoratori"],
  ["supervisor", "Preposto"],
  ["manager", "Dirigente"],
  ["rls", "RLS"],
  ["first_aid", "Primo soccorso"],
  ["firefighting", "Antincendio"],
  ["forklift", "Carrelli elevatori"],
  ["mewp", "PLE"],
  ["dpi_3rd_cat", "DPI III categoria"],
  ["working_at_height", "Lavori in quota"],
  ["confined_spaces", "Spazi confinati"],
  ["other", "Altro"],
];
const COURSE_LABEL = Object.fromEntries(COURSE_TYPES);

const ROLE_TYPES = [
  ["rspp", "RSPP"],
  ["aspp", "ASPP"],
  ["rls", "RLS"],
  ["supervisor", "Preposto"],
  ["safety_manager", "Dirigente sicurezza"],
  ["firefighting_officer", "Addetto antincendio"],
  ["first_aid_officer", "Addetto primo soccorso"],
  ["emergency_officer", "Addetto emergenze"],
  ["other", "Altro"],
];
const ROLE_LABEL = Object.fromEntries(ROLE_TYPES);

const VISIT_TYPES = [
  ["preventive", "Preventiva"],
  ["periodic", "Periodica"],
  ["task_change", "Cambio mansione"],
  ["return", "Rientro"],
  ["extraordinary", "Straordinaria"],
];
const VISIT_LABEL = Object.fromEntries(VISIT_TYPES);

const JUDGMENTS = [
  ["fit", "Idoneo"],
  ["fit_with_prescriptions", "Idoneo con prescrizioni/limitazioni"],
  ["temporarily_unfit", "Temporaneamente non idoneo"],
  ["unfit", "Non idoneo"],
];
const JUDGMENT_LABEL = Object.fromEntries(JUDGMENTS);

const SUBJECT_KINDS = [
  ["employee", "Dipendente"],
  ["employer", "Datore di lavoro"],
  ["external", "Soggetto esterno"],
];
const SUBJECT_LABEL = Object.fromEntries(SUBJECT_KINDS);

const DEEP_LINK_SECTIONS = { health: "health", training: "training", roles: "roles" };

/* ------------------------------------------------------------------ */
/* Helper                                                              */
/* ------------------------------------------------------------------ */
const fmtDate = (iso) => formatDateIT(iso, "—");

/** Traduce un `detail` di risposta HTTP in messaggio utente sicuro: mai
 * riportare payload sanitario. Rimpiazza messaggi di validazione contenenti
 * "clinico" o "diagnosi" con un testo neutro.
 */
const safeErr = (e) => {
  const msg = apiErr(e);
  if (typeof msg !== "string") return "Errore imprevisto";
  // Ridondanza di sicurezza: mai loggare/visualizzare parole cliniche
  const CLINICAL_RE = /(diagn|referto|anamn|patolog|malatt|terap)/i;
  if (CLINICAL_RE.test(msg)) {
    return "Dati non validi. Verifica di non aver inserito informazioni cliniche.";
  }
  return msg;
};

const Chip = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_TONE[status] || STATUS_TONE.missing}`}
    data-testid={`safety-chip-${status || "unknown"}`}
  >
    {STATUS_LABEL[status] || status || "—"}
  </span>
);

/* ------------------------------------------------------------------ */
/* COMPONENT ROOT                                                      */
/* ------------------------------------------------------------------ */
export default function EmployeeSafetyTab({
  employeeId,
  employee = null,
  canManage = true,
  isEmployeeSelf = false,
}) {
  const [dashboard, setDashboard] = useState(null);
  const [health, setHealth] = useState(null); // null=non caricato, []=vuoto, "forbidden"
  const [training, setTraining] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("health");
  const [dialogKind, setDialogKind] = useState(null); // "health"|"training"|"role"
  const [dialogRecord, setDialogRecord] = useState(null); // record originale se rinnovo
  const [highlightId, setHighlightId] = useState(null);

  const isEmployee = isEmployeeSelf;
  const canWriteHealth = canManage && !isEmployee;
  const canWriteSafety = canManage && !isEmployee;

  /* -------- Load -------- */
  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [d, t, r] = await Promise.all([
        api.get(`/employees/${employeeId}/safety/dashboard`).then((x) => x.data),
        api.get(`/employees/${employeeId}/safety/training`).then((x) => x.data),
        api.get(`/employees/${employeeId}/safety/roles`).then((x) => x.data),
      ]);
      setDashboard(d);
      setTraining(Array.isArray(t) ? t : []);
      setRoles(Array.isArray(r) ? r : []);
    } catch (e) {
      toast.error(safeErr(e));
    }
    // Health separato: può essere 403 per employee o per ruoli senza permesso
    try {
      const h = await api
        .get(`/employees/${employeeId}/safety/health`)
        .then((x) => x.data);
      setHealth(Array.isArray(h) ? h : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setHealth("forbidden");
      } else {
        setHealth([]);
      }
    }
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  /* -------- Deep-link ?section=&record= -------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sec = params.get("section");
    const rec = params.get("record");
    if (sec && DEEP_LINK_SECTIONS[sec]) {
      setTab(sec);
      if (rec) {
        setHighlightId(rec);
        setTimeout(() => setHighlightId(null), 4000);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="safety-row-${rec}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    }
  }, [health, training, roles]);

  /* -------- Handlers -------- */
  const openAdd = (kind) => { setDialogRecord(null); setDialogKind(kind); };
  const openRenew = (kind, rec) => {
    setDialogRecord({ ...rec, __renew: true, __kind: kind });
    setDialogKind(kind);
  };

  const deleteRecord = async (kind, id) => {
    if (!window.confirm("Confermi la cancellazione (soft delete)?")) return;
    try {
      const url = kind === "role"
        ? `/safety/roles/${id}`
        : `/employees/${employeeId}/safety/${kind}/${id}`;
      await api.delete(url);
      toast.success("Record eliminato");
      load();
    } catch (e) { toast.error(safeErr(e)); }
  };

  /* -------- Layout -------- */
  const tabButtons = [
    ["health", "Sorveglianza sanitaria", Stethoscope],
    ["training", "Formazione", GraduationCap],
    ["roles", "Incarichi", UserCheck],
  ];

  const addBtnLabel = { health: "Nuova visita", training: "Nuova formazione", roles: "Nuovo incarico" }[tab];
  const addBtnKind = tab === "roles" ? "role" : tab;
  const canAddCurrentTab = (tab === "health" ? canWriteHealth : canWriteSafety);

  return (
    <div className="space-y-4" data-testid="employee-safety-tab">
      {/* ==== Banner KPI ==== */}
      <SafetyDashboardBanner
        dashboard={dashboard}
        loading={loading}
        healthAccessDenied={health === "forbidden"}
        isEmployee={isEmployee}
        onQuickJump={(k) => setTab(k)}
      />

      {/* ==== Tab switcher + Add button ==== */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap" role="tablist">
          {tabButtons.map(([k, l, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              role="tab"
              aria-selected={tab === k}
              data-testid={`safety-tab-${k}`}
              className={`px-3 py-1.5 text-sm rounded-md font-medium inline-flex items-center gap-1.5 transition-colors ${
                tab === k
                  ? "bg-emerald-800 text-white shadow-sm"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
        {canAddCurrentTab && (
          <Button
            size="sm"
            onClick={() => openAdd(addBtnKind)}
            className="bg-emerald-800 hover:bg-emerald-700 text-white"
            data-testid={`safety-add-${addBtnKind}-btn`}
          >
            <Plus size={14} className="mr-1" /> {addBtnLabel}
          </Button>
        )}
      </div>

      {/* ==== Section content ==== */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {tab === "health" && (
            <HealthSection
              rows={health}
              highlight={highlightId}
              canManage={canWriteHealth}
              onRenew={(r) => openRenew("health", r)}
              onDelete={(r) => deleteRecord("health", r.id)}
            />
          )}
          {tab === "training" && (
            <TrainingSection
              rows={training}
              highlight={highlightId}
              canManage={canWriteSafety}
              onRenew={(r) => openRenew("training", r)}
              onDelete={(r) => deleteRecord("training", r.id)}
            />
          )}
          {tab === "roles" && (
            <RolesSection
              rows={roles}
              highlight={highlightId}
              canManage={canWriteSafety}
              onDelete={(r) => deleteRecord("role", r.id)}
            />
          )}
        </CardContent>
      </Card>

      {/* ==== Dialog ==== */}
      {dialogKind && (
        <SafetyFormDialog
          kind={dialogKind}
          employeeId={employeeId}
          existing={dialogRecord}
          onClose={() => { setDialogKind(null); setDialogRecord(null); }}
          onSaved={() => { setDialogKind(null); setDialogRecord(null); load(); }}
        />
      )}
    </div>
  );
}

/* ==================================================================== */
/* Banner KPI                                                            */
/* ==================================================================== */
function SafetyDashboardBanner({ dashboard, loading, healthAccessDenied, isEmployee, onQuickJump }) {
  const health = dashboard?.health;
  const training = dashboard?.training;
  const roles = dashboard?.roles;
  const next = dashboard?.upcoming_deadlines?.[0];

  return (
    <Card className="border-emerald-200 bg-emerald-50/50" data-testid="safety-dashboard-banner">
      <CardContent className="p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {/* Sorveglianza sanitaria */}
        {healthAccessDenied ? (
          <div
            className="rounded-md border border-slate-200 p-3 bg-white/60"
            data-testid="safety-kpi-health-forbidden"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase text-slate-600 font-semibold">
              <ShieldAlert size={12} /> Sorveglianza
            </div>
            <div className="text-sm text-slate-500 mt-2">
              Dati riservati
            </div>
          </div>
        ) : (
          <KpiCard
            testid="safety-kpi-health"
            icon={Stethoscope}
            title="Sorveglianza"
            valueNode={
              loading ? (
                <span className="text-slate-400 text-sm">…</span>
              ) : health ? (
                <Chip status={health.latest_status} />
              ) : (
                <span className="text-sm text-slate-500">
                  {isEmployee ? "N/D" : "Non richiesta"}
                </span>
              )
            }
            subNode={health?.next_visit_date ? `Prossima: ${fmtDate(health.next_visit_date)}` : null}
            onClick={() => onQuickJump("health")}
          />
        )}

        {/* Formazione */}
        <KpiCard
          testid="safety-kpi-training"
          icon={GraduationCap}
          title="Formazione"
          valueNode={
            <div className="text-lg font-bold">
              {training?.total ?? 0}
              <span className="text-xs font-normal ml-1 text-slate-600">corsi</span>
            </div>
          }
          subNode={
            (training?.expiring || 0) + (training?.expired || 0) > 0
              ? `${training.expiring} in scadenza · ${training.expired} scaduti`
              : "Tutti regolari"
          }
          onClick={() => onQuickJump("training")}
        />

        {/* Incarichi */}
        <KpiCard
          testid="safety-kpi-roles"
          icon={UserCheck}
          title="Incarichi"
          valueNode={
            <div className="text-lg font-bold">
              {roles?.active_count ?? 0}
              <span className="text-xs font-normal ml-1 text-slate-600">attivi</span>
            </div>
          }
          subNode={
            (roles?.expiring_count || 0) > 0 ? `${roles.expiring_count} in scadenza` : "Nessuno in scadenza"
          }
          onClick={() => onQuickJump("roles")}
        />

        {/* Prossima scadenza */}
        <KpiCard
          testid="safety-kpi-next"
          icon={CalendarClock}
          title="Prossima scadenza"
          valueNode={
            next ? (
              <div className="text-sm font-bold">{fmtDate(next.date)}</div>
            ) : (
              <div className="text-sm text-slate-500">Nessuna 90gg</div>
            )
          }
          subNode={
            next
              ? `${labelForUpcoming(next)} · ${next.days_left}gg`
              : null
          }
          onClick={() => {
            if (!next) return;
            const map = { health: "health", training: "training", role: "roles" };
            onQuickJump(map[next.type] || "health");
          }}
        />
      </CardContent>
    </Card>
  );
}

function KpiCard({ testid, icon: Icon, title, valueNode, subNode, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-md border p-3 hover:bg-white transition-colors bg-white/60 border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-1"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase text-emerald-800 font-semibold">
        <Icon size={12} /> {title}
      </div>
      <div className="mt-1 text-slate-900">{valueNode}</div>
      {subNode && <div className="mt-1 text-[11px] text-slate-600">{subNode}</div>}
    </button>
  );
}

function labelForUpcoming(u) {
  if (u.type === "health") return "Visita medica";
  if (u.type === "training") return COURSE_LABEL[u.course_type] || "Formazione";
  if (u.type === "role") return ROLE_LABEL[u.role_type] || "Incarico";
  return u.type;
}

/* ==================================================================== */
/* Health section                                                        */
/* ==================================================================== */
function HealthSection({ rows, highlight, canManage, onRenew, onDelete }) {
  if (rows === "forbidden") {
    return (
      <div className="p-6 text-center text-sm text-slate-500" data-testid="safety-health-forbidden">
        <ShieldAlert className="mx-auto mb-2 text-slate-400" size={24} />
        Non sei autorizzato a visualizzare i dati sanitari di questo dipendente.
        <div className="text-[11px] text-slate-400 mt-1">
          La visibilità è regolata dalle impostazioni aziendali (GDPR art. 9).
        </div>
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <EmptyState msg="Nessuna visita registrata" testid="safety-health-empty" />;
  }
  return (
    <SectionTable
      rows={rows}
      highlight={highlight}
      columns={[
        { k: "visit_type", l: "Tipo", render: (v) => VISIT_LABEL[v] || v || "—" },
        { k: "last_visit_date", l: "Ultima", fmt: fmtDate },
        { k: "next_visit_date", l: "Prossima", fmt: fmtDate },
        { k: "judgment", l: "Giudizio", render: (v) => JUDGMENT_LABEL[v] || v || "—" },
        { k: "status", l: "Stato", render: (v) => <Chip status={v} /> },
      ]}
      onRenew={canManage ? onRenew : null}
      onDelete={canManage ? onDelete : null}
    />
  );
}

/* ==================================================================== */
/* Training section                                                      */
/* ==================================================================== */
function TrainingSection({ rows, highlight, canManage, onRenew, onDelete }) {
  if (!rows || rows.length === 0) {
    return <EmptyState msg="Nessun corso registrato" testid="safety-training-empty" />;
  }
  return (
    <SectionTable
      rows={rows}
      highlight={highlight}
      columns={[
        { k: "course_type", l: "Corso", render: (v) => COURSE_LABEL[v] || v || "—" },
        { k: "provider_name", l: "Ente" },
        { k: "execution_date", l: "Data", fmt: fmtDate },
        { k: "expiry_date", l: "Scadenza", fmt: fmtDate },
        { k: "status", l: "Stato", render: (v) => <Chip status={v} /> },
      ]}
      onRenew={canManage ? onRenew : null}
      onDelete={canManage ? onDelete : null}
    />
  );
}

/* ==================================================================== */
/* Roles section                                                         */
/* ==================================================================== */
function RolesSection({ rows, highlight, canManage, onDelete }) {
  if (!rows || rows.length === 0) {
    return <EmptyState msg="Nessun incarico registrato" testid="safety-roles-empty" />;
  }
  return (
    <SectionTable
      rows={rows}
      highlight={highlight}
      columns={[
        { k: "role_type", l: "Ruolo", render: (v) => ROLE_LABEL[v] || v || "—" },
        { k: "subject_kind", l: "Soggetto",
          render: (v, row) => SUBJECT_LABEL[v] || v || "—" },
        { k: "subject_name", l: "Nominativo", render: (v, row) => v || (row.subject_kind === "employee" ? "—" : "—") },
        { k: "nomination_date", l: "Nomina", fmt: fmtDate },
        { k: "expiry_date", l: "Scadenza", fmt: fmtDate },
        { k: "status", l: "Stato", render: (v) => <Chip status={v} /> },
      ]}
      onDelete={canManage ? onDelete : null}
    />
  );
}

/* ==================================================================== */
/* Table generica riusabile                                              */
/* ==================================================================== */
function SectionTable({ rows, columns, onRenew, onDelete, highlight }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((c) => (
              <th key={c.k} className="px-3 py-2 text-left text-[11px] uppercase text-slate-600 whitespace-nowrap">
                {c.l}
              </th>
            ))}
            <th className="px-3 py-2 w-24" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`safety-row-${r.id}`}
              className={`border-t ${highlight === r.id ? "bg-yellow-50 ring-2 ring-yellow-300" : "hover:bg-slate-50/60"}`}
            >
              {columns.map((c) => (
                <td key={c.k} className="px-3 py-2 align-middle">
                  {c.render ? c.render(r[c.k], r) : c.fmt ? c.fmt(r[c.k]) : r[c.k] || "—"}
                </td>
              ))}
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {r.document_id && (
                  <a
                    href={`/documenti/${r.document_id}`}
                    onClick={(e) => e.preventDefault()}
                    className="inline-flex"
                    title="Apri documento"
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      data-testid={`safety-open-document-${r.id}`}
                    >
                      <FileText size={13} />
                    </Button>
                  </a>
                )}
                {onRenew && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-emerald-700"
                    onClick={() => onRenew(r)}
                    title="Rinnova (nuovo record)"
                    data-testid={`safety-renew-${r.id}`}
                  >
                    <RefreshCw size={13} />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-red-700"
                    onClick={() => onDelete(r)}
                    title="Elimina (soft delete)"
                    data-testid={`safety-delete-${r.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ msg, testid }) {
  return (
    <div className="p-8 text-center text-sm text-slate-500" data-testid={testid}>
      {msg}
    </div>
  );
}

/* ==================================================================== */
/* Dialog form                                                           */
/* ==================================================================== */
function SafetyFormDialog({ kind, employeeId, existing, onClose, onSaved }) {
  const isRenew = !!existing?.__renew;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => {
    if (isRenew) {
      // Precompila con i dati precedenti ma resetta le date "puntuali"
      const base = { ...existing };
      delete base.__renew;
      delete base.__kind;
      delete base.id;
      // Non ereditare le vecchie date: si mette la nuova
      if (kind === "health") {
        base.last_visit_date = "";
        base.next_visit_date = "";
        base.judgment_date = "";
        base.judgment_review_date = "";
      } else if (kind === "training") {
        base.execution_date = "";
        base.certificate_date = "";
        base.expiry_date = "";
      }
      return base;
    }
    if (kind === "health") return {
      surveillance_required: true,
      visit_type: "periodic",
      last_visit_date: "",
      next_visit_date: "",
      judgment: "fit",
      judgment_date: "",
      judgment_review_date: "",
      doctor_name: "",
      task_description: "",
      prescriptions_short: "",
      admin_notes: "",
      document_id: "",
    };
    if (kind === "training") return {
      course_type: "general",
      course_description: "",
      provider_name: "",
      execution_date: "",
      certificate_date: "",
      expiry_date: "",
      duration_hours: "",
      requires_refresh: false,
      admin_notes: "",
      document_id: "",
    };
    // role
    return {
      role_type: "rspp",
      subject_kind: "employee",
      subject_name: "",
      subject_fiscal_code: "",
      nomination_date: "",
      effective_from: "",
      expiry_date: "",
      admin_notes: "",
      document_id: "",
    };
  });

  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form };
      // Normalizza stringhe vuote -> null per campi opzionali
      const nullable = [
        "last_visit_date", "next_visit_date", "judgment_date", "judgment_review_date",
        "certificate_date", "expiry_date", "document_id",
        "doctor_name", "doctor_code", "task_description", "protocol_code",
        "prescriptions_short", "admin_notes",
        "course_description", "provider_name",
        "subject_name", "subject_fiscal_code",
      ];
      nullable.forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (payload.duration_hours === "" || payload.duration_hours === undefined) {
        payload.duration_hours = null;
      }

      let url;
      if (isRenew && kind !== "role") {
        url = `/employees/${employeeId}/safety/${kind}/${existing.id}/renew`;
      } else if (kind === "role") {
        url = `/employees/${employeeId}/safety/roles`;
      } else {
        url = `/employees/${employeeId}/safety/${kind}`;
      }
      await api.post(url, payload);
      toast.success(isRenew ? "Rinnovo registrato" : "Record salvato");
      onSaved();
    } catch (e) {
      toast.error(safeErr(e));
    } finally {
      setBusy(false);
    }
  };

  const title = {
    health: isRenew ? "Rinnovo visita medica" : "Nuova visita medica / giudizio",
    training: isRenew ? "Rinnovo formazione" : "Nuova formazione",
    role: "Nuovo incarico / nomina",
  }[kind];

  return (
    <SafeFormDialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      isDirty={dirty}
      size="xl"
      title={title}
      data-testid={`safety-dialog-${kind}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        {kind === "health" && <HealthFormFields form={form} set={set} />}
        {kind === "training" && <TrainingFormFields form={form} set={set} />}
        {kind === "role" && <RoleFormFields form={form} set={set} />}
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t mt-4">
        <Button variant="outline" onClick={onClose} disabled={busy} data-testid="safety-cancel-btn">
          Annulla
        </Button>
        <Button
          className="bg-emerald-800 hover:bg-emerald-700 text-white"
          onClick={save}
          disabled={busy}
          data-testid="safety-save-btn"
        >
          {busy ? "Salvataggio…" : isRenew ? "Rinnova" : "Salva"}
        </Button>
      </div>
    </SafeFormDialog>
  );
}

/* -------- Sub-form: Health -------- */
function HealthFormFields({ form, set }) {
  return (
    <>
      <Field label="Tipo visita">
        <Select value={form.visit_type} onValueChange={(v) => set("visit_type", v)}>
          <SelectTrigger data-testid="health-visit-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {VISIT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Giudizio">
        <Select value={form.judgment} onValueChange={(v) => set("judgment", v)}>
          <SelectTrigger data-testid="health-judgment"><SelectValue /></SelectTrigger>
          <SelectContent>
            {JUDGMENTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Medico competente">
        <Input value={form.doctor_name || ""}
               onChange={(e) => set("doctor_name", e.target.value)}
               data-testid="health-doctor-name" />
      </Field>
      <Field label="Mansione sottoposta">
        <Input value={form.task_description || ""}
               onChange={(e) => set("task_description", e.target.value)}
               data-testid="health-task" />
      </Field>
      <Field label="Data ultima visita">
        <Input type="date" value={form.last_visit_date || ""}
               onChange={(e) => set("last_visit_date", e.target.value)}
               data-testid="health-last-visit" />
      </Field>
      <Field label="Prossima visita">
        <Input type="date" value={form.next_visit_date || ""}
               onChange={(e) => set("next_visit_date", e.target.value)}
               data-testid="health-next-visit" />
      </Field>
      <Field label="Data giudizio">
        <Input type="date" value={form.judgment_date || ""}
               onChange={(e) => set("judgment_date", e.target.value)} />
      </Field>
      <Field label="Data revisione (opz.)">
        <Input type="date" value={form.judgment_review_date || ""}
               onChange={(e) => set("judgment_review_date", e.target.value)} />
      </Field>
      <Field label="Prescrizioni / limitazioni operative" className="sm:col-span-2">
        <Textarea
          rows={2}
          maxLength={500}
          value={form.prescriptions_short || ""}
          onChange={(e) => set("prescriptions_short", e.target.value)}
          placeholder="es. non movimentazione carichi &gt; 10 kg"
          data-testid="health-prescriptions"
        />
        <div className="text-[10px] text-red-700 mt-1">
          Solo prescrizioni/limitazioni utili alla gestione lavorativa.
          Non inserire diagnosi, referti o dati clinici (GDPR art. 9).
        </div>
      </Field>
      <Field label="Note amministrative (interne)" className="sm:col-span-2">
        <Textarea
          rows={2}
          maxLength={1000}
          value={form.admin_notes || ""}
          onChange={(e) => set("admin_notes", e.target.value)}
        />
      </Field>
      <label className="flex items-center gap-2 sm:col-span-2 text-sm">
        <Checkbox
          checked={!!form.surveillance_required}
          onCheckedChange={(v) => set("surveillance_required", !!v)}
          data-testid="health-surv-required"
        />
        Il dipendente è soggetto a sorveglianza sanitaria
      </label>
    </>
  );
}

/* -------- Sub-form: Training -------- */
function TrainingFormFields({ form, set }) {
  return (
    <>
      <Field label="Tipologia corso">
        <Select value={form.course_type} onValueChange={(v) => set("course_type", v)}>
          <SelectTrigger data-testid="training-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COURSE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Descrizione">
        <Input value={form.course_description || ""}
               onChange={(e) => set("course_description", e.target.value)}
               data-testid="training-description" />
      </Field>
      <Field label="Ente formatore">
        <Input value={form.provider_name || ""}
               onChange={(e) => set("provider_name", e.target.value)}
               data-testid="training-provider" />
      </Field>
      <Field label={<span className="inline-flex items-center gap-2">Durata (ore) <DurationFormatBadge data-testid="training-duration-format-badge" /></span>}>
        <DurationInput
          value={form.duration_hours ?? ""}
          onCommit={(n) => set("duration_hours", n ?? "")}
          data-testid="training-duration"
        />
      </Field>
      <Field label="Data svolgimento">
        <Input type="date" value={form.execution_date || ""}
               onChange={(e) => set("execution_date", e.target.value)}
               data-testid="training-execution-date" />
      </Field>
      <Field label="Data attestato">
        <Input type="date" value={form.certificate_date || ""}
               onChange={(e) => set("certificate_date", e.target.value)} />
      </Field>
      <Field label="Data scadenza">
        <Input type="date" value={form.expiry_date || ""}
               onChange={(e) => set("expiry_date", e.target.value)}
               data-testid="training-expiry-date" />
      </Field>
      <label className="flex items-center gap-2 text-sm mt-1">
        <Checkbox
          checked={!!form.requires_refresh}
          onCheckedChange={(v) => set("requires_refresh", !!v)}
        />
        Aggiornamento richiesto
      </label>
      <Field label="Note" className="sm:col-span-2">
        <Textarea rows={2} value={form.admin_notes || ""}
                  onChange={(e) => set("admin_notes", e.target.value)} />
      </Field>
    </>
  );
}

/* -------- Sub-form: Role -------- */
function RoleFormFields({ form, set }) {
  return (
    <>
      <Field label="Tipo incarico">
        <Select value={form.role_type} onValueChange={(v) => set("role_type", v)}>
          <SelectTrigger data-testid="role-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Soggetto">
        <Select value={form.subject_kind} onValueChange={(v) => set("subject_kind", v)}>
          <SelectTrigger data-testid="role-subject-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SUBJECT_KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      {form.subject_kind !== "employee" && (
        <>
          <Field label="Nominativo">
            <Input value={form.subject_name || ""}
                   onChange={(e) => set("subject_name", e.target.value)}
                   data-testid="role-subject-name" />
          </Field>
          <Field label="Codice fiscale (opz.)">
            <Input value={form.subject_fiscal_code || ""}
                   onChange={(e) => set("subject_fiscal_code", e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Data nomina">
        <Input type="date" value={form.nomination_date || ""}
               onChange={(e) => set("nomination_date", e.target.value)}
               data-testid="role-nomination-date" />
      </Field>
      <Field label="Decorrenza">
        <Input type="date" value={form.effective_from || ""}
               onChange={(e) => set("effective_from", e.target.value)}
               data-testid="role-effective-from" />
      </Field>
      <Field label="Data scadenza (opz.)">
        <Input type="date" value={form.expiry_date || ""}
               onChange={(e) => set("expiry_date", e.target.value)} />
      </Field>
      <Field label="Note" className="sm:col-span-2">
        <Textarea rows={2} value={form.admin_notes || ""}
                  onChange={(e) => set("admin_notes", e.target.value)} />
      </Field>
    </>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
